<template>
  <NuxtLink
    :to="link"
    class="group block h-full cursor-pointer"
    :aria-label="`Відкрити товар: ${product.title || 'Товар'}${
      variantLabel ? ' — ' + variantLabel : ''
    }`"
    @click="onCardClick"
  >
    <div
      class="border border-subtle rounded-xl overflow-hidden flex h-full flex-col"
    >
      <div class="relative w-full pt-[133%] bg-gray-100">
        <!-- Бажане живе в localStorage, тож сервер про нього не знає:
             без ClientOnly сервер малює порожньо, а перший клієнтський
             рендер — сердечко, і Vue лається на розбіжність гідрації. -->
        <ClientOnly>
          <div v-if="isWished" class="absolute right-2 top-2 z-10">
            <Heart :size="20" fill="currentColor" class="text-pink-200" />
          </div>
        </ClientOnly>

        <!-- Badges: stack from the same place as "Немає в наявності" and go вверх -->
        <div
          class="absolute left-2 bottom-2 z-10 flex flex-col items-start gap-2"
        >
          <div
            v-if="isExclusive"
            class="rounded-lg bg-pink-200/90 px-2.5 py-1 text-xs font-semibold text-black"
          >
            {{ exclusiveBadgeText }}
          </div>

          <div
            v-if="isLonelyBeauty"
            class="rounded-lg bg-pink-200/90 px-2.5 py-1 text-xs font-semibold text-black"
          >
            Самотня красуня
          </div>

          <div
            v-if="isOutOfStock"
            class="rounded-lg bg-gray-200/90 px-2.5 py-1 text-xs font-medium text-gray-800"
          >
            Немає в наявності
          </div>
        </div>

        <img
          v-if="coverUrl"
          ref="imgRef"
          :src="imgSrc"
          :srcset="imgSrcset"
          sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
          :alt="product.title || 'Товар'"
          class="transition group-hover:scale-[1.02] absolute inset-0 h-full w-full object-cover"
          width="300"
          height="400"
          loading="lazy"
          decoding="async"
          @error="onImgError"
        />

        <div
          v-else
          class="absolute inset-0 grid place-items-center text-gray-400"
        >
          <ImageIcon :size="24" />
        </div>
      </div>

      <div class="flex grow flex-col p-3 relative">
        <!-- Фіксована зона під заголовок (2 рядки) + підзаголовок -->
        <div class="min-h-[4.25rem] flex flex-col justify-start">
          <h3
            class="text-[16px] font-medium text-gray-900 clamp-2 underline-offset-2 leading-tight"
            :title="product.title"
          >
            {{ product.title }}
          </h3>
          <p v-if="variantLabel" class="text-xs text-gray-500 mt-0.5">
            {{ variantLabel }}
          </p>
        </div>

        <div
          class="mt-auto flex items-end justify-between gap-3 pt-2 relative z-10"
        >
          <div class="text-[15px] font-semibold text-gray-900">
            {{ formatPrice(priceToShow, product.currency) }}
          </div>

          <div class="relative">
            <button
              ref="toggleBtnRef"
              type="button"
              class="inline-flex h-11 min-w-11 items-center justify-center rounded-full px-3 text-[15px] font-medium bg-pink-200 hover:bg-pink-200/80 active:scale-[.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-pink-200"
              :disabled="isOutOfStock"
              :aria-label="`Додати у кошик: ${product.title || 'Товар'}`"
              :aria-expanded="showPicker ? 'true' : 'false'"
              :aria-disabled="isOutOfStock ? 'true' : 'false'"
              @click.stop.prevent="onAddClick"
            >
              <ShoppingCart :size="20" />
              <span class="hidden sm:inline ml-2">В кошик</span>
            </button>
          </div>
        </div>

        <CatalogProductCardPicker
          v-model:open="showPicker"
          v-model:color="selectedColor"
          v-model:size="selectedSize"
          :colors="colorsList"
          :sizes="sizesList"
          :can-submit="canSubmit"
          :anchor-el="toggleBtnRef"
          @confirm="confirmAddToCart()"
        />
      </div>
    </div>
  </NuxtLink>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { Heart, ImageIcon, ShoppingCart } from "lucide-vue-next";
import { useRoute } from "vue-router";
import { useWishlistStore } from "~/stores/wishlist";
import { useTracking, buildGaItem } from "~/composables/useTracking";
import { formatPrice } from "~/utils/money";
import { useCardImage } from "~/composables/catalog/useCardImage";
import { useCardAddToCart } from "~/composables/catalog/useCardAddToCart";
import { useCardVariantView } from "~/composables/catalog/useCardVariantView";

const props = defineProps({
  product: { type: Object, required: true },
  variant: { type: Object, default: null },
});
const product = props.product;
const variant = computed(() => props.variant || null);

const wl = useWishlistStore();

const showPicker = ref(false);
const selectedSize = ref("");
const selectedColor = ref("");
const toggleBtnRef = ref(null);
const imgRef = ref(null);

const route = useRoute();
const { coverUrl, imgSrc, imgSrcset, onImgError } = useCardImage(
  () => product,
  () => variant.value,
);

/* ===== 🔵 GA4 + Meta Pixel e-commerce events ===== */
const { ga: gaLog, pixel: mpTrack } = useTracking();

function listName() {
  const p = route.path || "";
  if (p === "/") return "home";
  if (p.startsWith("/catalog")) return "catalog";
  if (p.startsWith("/wishlist")) return "wishlist";
  if (p.startsWith("/profile")) return "profile";
  return "site";
}

function gaItem(quantity = 1) {
  const v = variant.value;
  return buildGaItem({
    product,
    variant: v,
    variantFallback: [
      v?.color?.value ?? v?.color ?? selectedColor.value,
      v?.size ?? selectedSize.value,
    ],
    price: Number(priceToShow.value || 0),
    quantity,
    list: { name: listName(), id: route.fullPath },
  });
}

function onCardClick() {
  if (!import.meta.client) return;
  gaLog("select_item", {
    item_list_name: listName(),
    item_list_id: route.fullPath,
    items: [gaItem(1)],
  });
}

// індикатор «в бажаному» — по парі pid+vid
const isWished = computed(
  () => !!variant.value?.id && wl.hasPair(product.id, variant.value.id),
);

const {
  link,
  priceToShow,
  isExclusive,
  exclusiveBadgeText,
  isOutOfStock,
  isLonelyBeauty,
  sizesList,
  colorsList,
  variantLabel,
  needSize,
  needColor,
  canSubmit,
} = useCardVariantView(
  () => product,
  () => variant.value,
  selectedColor,
  selectedSize,
);

onMounted(() => {
  const c = variant.value?.color?.value ?? variant.value?.color ?? "";
  const s = variant.value?.size ?? "";
  if (c) selectedColor.value = String(c);
  if (s) selectedSize.value = String(s);
});

const { onAddClick, confirmAddToCart } = useCardAddToCart({
  product: () => product,
  variant: () => variant.value,
  selectedColor,
  selectedSize,
  needColor,
  needSize,
  canSubmit,
  priceToShow,
  isOutOfStock,
  coverUrl,
  imgRef,
  showPicker,
  onAdded: () => {
    gaLog("add_to_cart", {
      currency: "UAH",
      value: Number(priceToShow.value || 0) * 1,
      items: [gaItem(1)],
    });

    mpTrack("AddToCart", {
      content_type: "product",
      content_ids: [String(product.id || "")].filter(Boolean),
      content_name: String(product.title || ""),
      currency: String(product.currency || "UAH"),
      value: Number(priceToShow.value || 0),
      contents: [
        {
          id: String(product.id || ""),
          quantity: 1,
          item_price: Number(priceToShow.value || 0),
        },
      ].filter((x) => x.id),
    });
  },
});
</script>
